import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import PerformanceCard from './PerformanceCard';
import CircularGauge from './CircularGauge';
import ProgressBar from './ProgressBar';

const FinanceSection = ({ data, onNavigate, formatCurrency }) => {
  // Valeurs par défaut pour éviter les erreurs undefined
  const defaultData = {
    total_revenue_ytd: 0,
    total_costs_ytd: 0,
    gross_profit_ytd: 0,
    profit_margin_percentage: 0,
    cash_flow_current_month: 0,
    cash_flow_trend: 0,
    accounts_receivable: 0,
    accounts_payable: 0,
    revenue_growth_yoy: 0,
    roi_percentage: 0,
    budget_vs_actual: { budget: 0, actual: 0 },
    financial_health_score: 0,
    projected_revenue_ytd: 0,
    projected_profit_ytd: 0,
    break_even_point: { monthly_target: 0, current_position: 0 },
    costs_breakdown: { transport: 0, personnel: 0, infrastructure: 0, autres: 0 },
    expense_trends: [],
    working_capital: 0,
    debt_to_equity_ratio: 0,
    current_ratio: 0,
    quick_ratio: 0
  };

  // Fusion des données reçues avec les valeurs par défaut
  const mergedData = { ...defaultData, ...data };
  
  const {
    total_revenue_ytd,
    total_costs_ytd,
    gross_profit_ytd,
    profit_margin_percentage,
    cash_flow_current_month,
    cash_flow_trend,
    accounts_receivable,
    accounts_payable,
    revenue_growth_yoy,
    roi_percentage,
    budget_vs_actual,
    financial_health_score,
    projected_revenue_ytd,
    projected_profit_ytd,
    break_even_point,
    costs_breakdown,
    expense_trends,
    working_capital,
    debt_to_equity_ratio,
    current_ratio,
    quick_ratio
  } = mergedData;

  // Données pour le graphique de répartition des coûts
  const costsData = Object.entries(costs_breakdown).map(([category, amount]) => ({
    name: category.charAt(0).toUpperCase() + category.slice(1),
    value: amount,
    color: getCategoryColor(category)
  }));

  function getCategoryColor(category) {
    const colors = {
      'transport': '#3B82F6',
      'personnel': '#10B981',
      'infrastructure': '#F59E0B',
      'autres': '#EF4444'
    };
    return colors[category] || '#6B7280';
  }

  const getHealthColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-100', indicator: 'bg-green-500', text: 'text-green-700' };
    if (score >= 60) return { bg: 'bg-yellow-100', indicator: 'bg-yellow-500', text: 'text-yellow-700' };
    if (score >= 40) return { bg: 'bg-orange-100', indicator: 'bg-orange-500', text: 'text-orange-700' };
    return { bg: 'bg-red-100', indicator: 'bg-red-500', text: 'text-red-700' };
  };

  const healthStyle = getHealthColor(financial_health_score);

  const ratioData = [
    { name: 'Liquidité Générale', value: current_ratio, target: 2.0, icon: '💧' },
    { name: 'Liquidité Réduite', value: quick_ratio, target: 1.5, icon: '⚡' },
    { name: 'Endettement/Capitaux', value: debt_to_equity_ratio, target: 0.5, icon: '⚖️' }
  ];

  return (
    <div className="space-y-8">
      {/* En-tête Finance */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center">
          <span className="mr-3">💰</span>
          Finance - Vision Comptable & Stratégique
        </h2>
        
        {/* Score de santé financière */}
        <div className={`${healthStyle.bg} rounded-xl p-6 mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-lg font-semibold ${healthStyle.text}`}>Score de Santé Financière</h3>
              <p className="text-sm text-gray-600 mt-1">Indicateur composite de la performance financière</p>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold ${healthStyle.text}`}>
                {financial_health_score.toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">/100</div>
            </div>
          </div>
          <div className="w-full bg-white bg-opacity-50 rounded-full h-3 mt-4">
            <div
              className={`h-3 rounded-full ${healthStyle.indicator} transition-all duration-500`}
              style={{ width: `${financial_health_score}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PerformanceCard
          title="Chiffre d'Affaires YTD"
          value={total_revenue_ytd}
          unit="FCFA"
          change={`Croissance: ${revenue_growth_yoy.toFixed(1)}%`}
          changeLabel="vs année précédente"
          icon="📈"
          color="green"
          onClick={() => onNavigate('revenue-details')}
        />
        
        <PerformanceCard
          title="Marge Brute YTD"
          value={gross_profit_ytd}
          unit="FCFA"
          change={`Marge: ${profit_margin_percentage.toFixed(1)}%`}
          changeLabel="du CA"
          icon="💎"
          color="blue"
          onClick={() => onNavigate('profit-analysis')}
        />
        
        <PerformanceCard
          title="Cash Flow Mensuel"
          value={cash_flow_current_month}
          unit="FCFA"
          change={`Tendance: ${cash_flow_trend >= 0 ? '+' : ''}${cash_flow_trend.toFixed(1)}%`}
          changeLabel="évolution"
          icon="💳"
          color={cash_flow_current_month > 0 ? "green" : "red"}
          onClick={() => onNavigate('cash-flow')}
        />
        
        <PerformanceCard
          title="ROI"
          value={roi_percentage.toFixed(1)}
          unit="%"
          change={roi_percentage >= 15 ? "Excellent" : roi_percentage >= 10 ? "Bon" : "À améliorer"}
          changeLabel="performance"
          icon="🎯"
          color={roi_percentage >= 15 ? "green" : roi_percentage >= 10 ? "yellow" : "red"}
          onClick={() => onNavigate('roi-details')}
        />
      </div>

      {/* Projections et Budget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">🔮</span>
            Projections Fin d'Année
          </h3>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">CA Projeté</div>
                <div className="text-sm text-gray-600">Basé sur la tendance actuelle</div>
              </div>
              <div className="text-xl font-bold text-blue-600">
                {formatCurrency(projected_revenue_ytd)}
              </div>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Profit Projeté</div>
                <div className="text-sm text-gray-600">Estimation fin d'année</div>
              </div>
              <div className="text-xl font-bold text-green-600">
                {formatCurrency(projected_profit_ytd)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">📊</span>
            Budget vs Réel
          </h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Budget Annuel</span>
                <span>{formatCurrency(budget_vs_actual.budget)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium mb-2">
                <span>Réalisé YTD</span>
                <span>{formatCurrency(budget_vs_actual.actual)}</span>
              </div>
              <ProgressBar
                title=""
                progress={(budget_vs_actual.actual / budget_vs_actual.budget) * 100}
                current={budget_vs_actual.actual}
                target={budget_vs_actual.budget}
                unit="FCFA"
                color="#22C55E"
                height={8}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Répartition des coûts et Cash Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">📋</span>
            Répartition des Coûts
          </h3>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={costsData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {costsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 grid grid-cols-2 gap-2">
            {costsData.map((cost, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <div className="w-3 h-3 rounded-full" style={{backgroundColor: cost.color}}></div>
                <span>{cost.name}: {formatCurrency(cost.value)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">💰</span>
            Créances & Dettes
          </h3>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">💳</span>
                <div>
                  <div className="font-medium">Créances Clients</div>
                  <div className="text-sm text-gray-600">À recevoir</div>
                </div>
              </div>
              <div className="text-lg font-bold text-green-600">
                +{formatCurrency(accounts_receivable)}
              </div>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">📋</span>
                <div>
                  <div className="font-medium">Dettes Fournisseurs</div>
                  <div className="text-sm text-gray-600">À payer</div>
                </div>
              </div>
              <div className="text-lg font-bold text-red-600">
                -{formatCurrency(accounts_payable)}
              </div>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">⚖️</span>
                <div>
                  <div className="font-medium">Besoin en Fonds de Roulement</div>
                  <div className="text-sm text-gray-600">Créances - Dettes</div>
                </div>
              </div>
              <div className={`text-lg font-bold ${working_capital >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {working_capital >= 0 ? '+' : ''}{formatCurrency(working_capital)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ratios financiers */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <span className="mr-2">📐</span>
          Ratios Financiers Clés
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ratioData.map((ratio, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl mb-2">{ratio.icon}</div>
              <div className="text-2xl font-bold text-gray-900">
                {ratio.value.toFixed(2)}
              </div>
              <div className="text-sm text-gray-600 mb-2">{ratio.name}</div>
              <div className={`text-xs px-2 py-1 rounded-full ${
                ratio.value >= ratio.target ? 'bg-green-100 text-green-800' : 
                ratio.value >= ratio.target * 0.8 ? 'bg-yellow-100 text-yellow-800' : 
                'bg-red-100 text-red-800'
              }`}>
                Cible: {ratio.target.toFixed(1)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Point mort */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">🎯</span>
          Analyse du Point Mort
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Objectif Mensuel (Coûts)</div>
            <div className="text-xl font-bold text-gray-900">
              {formatCurrency(break_even_point.monthly_target)}
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Position Actuelle</div>
            <div className={`text-xl font-bold ${
              break_even_point.current_position >= break_even_point.monthly_target ? 
              'text-green-600' : 'text-red-600'
            }`}>
              {formatCurrency(break_even_point.current_position)}
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <ProgressBar
            title="Progression vers l'équilibre mensuel"
            progress={(break_even_point.current_position / break_even_point.monthly_target) * 100}
            current={break_even_point.current_position}
            target={break_even_point.monthly_target}
            unit="FCFA"
            color={break_even_point.current_position >= break_even_point.monthly_target ? '#22C55E' : '#F97316'}
            height={10}
          />
        </div>
      </div>

      {/* Actions recommandées */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">⚡</span>
          Actions Recommandées
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => onNavigate('cash-flow-forecast')}
            className="bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Prévisions de Trésorerie</div>
                <div className="text-sm opacity-90">Analyse des flux futurs</div>
              </div>
              <span className="text-2xl">📊</span>
            </div>
          </button>
          
          <button
            onClick={() => onNavigate('cost-optimization')}
            className="bg-orange-500 text-white p-4 rounded-lg hover:bg-orange-600 transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Optimisation des Coûts</div>
                <div className="text-sm opacity-90">Réduire les dépenses</div>
              </div>
              <span className="text-2xl">✂️</span>
            </div>
          </button>
          
          <button
            onClick={() => onNavigate('investment-analysis')}
            className="bg-green-500 text-white p-4 rounded-lg hover:bg-green-600 transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Analyse Investissements</div>
                <div className="text-sm opacity-90">ROI et rentabilité</div>
              </div>
              <span className="text-2xl">💰</span>
            </div>
          </button>
          
          <button
            onClick={() => onNavigate('budget-planning')}
            className="bg-purple-500 text-white p-4 rounded-lg hover:bg-purple-600 transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Planification Budget</div>
                <div className="text-sm opacity-90">Budget année suivante</div>
              </div>
              <span className="text-2xl">📋</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinanceSection;